import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, differenceInDays, addDays, startOfDay } from 'date-fns';
import { Trash2, ArrowRight, ShoppingBag, Calendar, FileText, AlertTriangle, Shield, Info, X, MapPin, User, Building2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import RentalCalendar from '../components/RentalCalendar';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MIN_RENTAL_DAYS = 3;
const DAILY_RATE = 59.99;
const INSURANCE_DAILY_RATE = 25;
const EXPEDITED_CUTOFF_DAYS = 6;
const STANDARD_CUTOFF_DAYS = 7;
const BLOCKED_DAYS = 2;
const EXPEDITED_SHIPPING_FEE = 50;
const SECURITY_DEPOSIT_AMOUNT = 150;

// Insurance Modal Component
const InsuranceModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="insurance-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border-2 border-[#C6A87C] max-w-lg w-full p-0 shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#C6A87C] to-[#D4B88F] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-black" />
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight text-black">
                Damage Protection
              </h2>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-black/10 rounded transition-colors"
              data-testid="close-insurance-modal"
            >
              <X className="w-6 h-6 text-black" />
            </button>
          </div>
          <p className="text-black/80 mt-2 text-sm">$25/day coverage for peace of mind</p>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Covered Section */}
          <div className="space-y-3">
            <h3 className="text-[#C6A87C] font-semibold uppercase tracking-wider text-sm flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              What&apos;s Covered
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-3" style={{ color: '#333333' }}>
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Minor scuffs & tears from normal use</span>
              </li>
              <li className="flex items-start gap-3" style={{ color: '#333333' }}>
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Broken zippers & fasteners</span>
              </li>
              <li className="flex items-start gap-3" style={{ color: '#333333' }}>
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Theft (with police report)</span>
              </li>
              <li className="flex items-start gap-3" style={{ color: '#333333' }}>
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Professional cleaning included</span>
              </li>
            </ul>
          </div>
          
          {/* Not Covered Section */}
          <div className="space-y-3">
            <h3 className="text-[#C6A87C] font-semibold uppercase tracking-wider text-sm flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
              Not Covered
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-3" style={{ color: '#333333' }}>
                <span className="text-red-500 mt-0.5">✗</span>
                <span>Total loss / crash impacts (requires full retail replacement)</span>
              </li>
              <li className="flex items-start gap-3" style={{ color: '#333333' }}>
                <span className="text-red-500 mt-0.5">✗</span>
                <span>Negligence / stunt riding damage</span>
              </li>
            </ul>
          </div>
          
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs" style={{ color: '#666666' }}>
              Protection is charged per day for each item in your rental. Coverage begins when gear ships and ends upon return receipt.
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 pt-0">
          <Button 
            onClick={onClose}
            className="w-full bg-[#C6A87C] text-black hover:bg-[#D4B88F] font-bold uppercase tracking-wider"
          >
            Got It
          </Button>
        </div>
      </div>
    </div>
  );
};

// Shipping Form Component
const ShippingForm = ({ shippingInfo, setShippingInfo, errors }) => {
  return (
    <div className="space-y-4" data-testid="shipping-form">
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
        <MapPin className="w-5 h-5 text-[#C6A87C]" />
        <h3 className="font-['Barlow_Condensed'] text-lg font-semibold uppercase tracking-tight" style={{ color: '#333333' }}>
          Destination Shipping Info
        </h3>
      </div>
      
      {/* Recipient Name */}
      <div>
        <label className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2 block font-semibold">
          Recipient Name *
        </label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C6A87C]" />
          <input
            type="text"
            value={shippingInfo.recipientName}
            onChange={(e) => setShippingInfo(prev => ({ ...prev, recipientName: e.target.value }))}
            placeholder="Full name of recipient"
            className={`w-full h-12 pl-12 pr-4 bg-gray-50 border ${errors.recipientName ? 'border-red-500' : 'border-gray-200'} placeholder:text-gray-400 focus:border-[#C6A87C] focus:outline-none focus:ring-1 focus:ring-[#C6A87C] transition-colors`}
            style={{ color: '#333333' }}
            data-testid="shipping-recipient-name"
          />
        </div>
        {errors.recipientName && <p className="text-xs text-red-500 mt-1">{errors.recipientName}</p>}
      </div>

      {/* Email */}
      <div>
        <label className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2 block font-semibold">
          Email Address *
        </label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C6A87C]" />
          <input
            type="email"
            value={shippingInfo.customerEmail}
            onChange={(e) => setShippingInfo(prev => ({ ...prev, customerEmail: e.target.value }))}
            placeholder="For order updates & receipt"
            className={`w-full h-12 pl-12 pr-4 bg-gray-50 border ${errors.customerEmail ? 'border-red-500' : 'border-gray-200'} placeholder:text-gray-400 focus:border-[#C6A87C] focus:outline-none focus:ring-1 focus:ring-[#C6A87C] transition-colors`}
            style={{ color: '#333333' }}
            data-testid="shipping-customer-email"
          />
        </div>
        {errors.customerEmail && <p className="text-xs text-red-500 mt-1">{errors.customerEmail}</p>}
      </div>
      
      {/* Destination Type */}
      <div>
        <label className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2 block font-semibold">
          Destination Type *
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setShippingInfo(prev => ({ ...prev, destinationType: 'hotel' }))}
            className={`h-12 flex items-center justify-center gap-2 border ${
              shippingInfo.destinationType === 'hotel' 
                ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]' 
                : 'bg-gray-50 border-gray-200 hover:border-[#C6A87C]'
            } transition-colors font-semibold uppercase tracking-wider text-sm`}
            style={shippingInfo.destinationType !== 'hotel' ? { color: '#333333' } : {}}
            data-testid="destination-hotel"
          >
            <Building2 className="w-4 h-4" />
            Hotel
          </button>
          <button
            type="button"
            onClick={() => setShippingInfo(prev => ({ ...prev, destinationType: 'residence' }))}
            className={`h-12 flex items-center justify-center gap-2 border ${
              shippingInfo.destinationType === 'residence' 
                ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]' 
                : 'bg-gray-50 border-gray-200 hover:border-[#C6A87C]'
            } transition-colors font-semibold uppercase tracking-wider text-sm`}
            style={shippingInfo.destinationType !== 'residence' ? { color: '#333333' } : {}}
            data-testid="destination-residence"
          >
            <MapPin className="w-4 h-4" />
            Residence
          </button>
        </div>
        {errors.destinationType && <p className="text-xs text-red-500 mt-1">{errors.destinationType}</p>}
      </div>
      
      {/* Full Address */}
      <div>
        <label className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2 block font-semibold">
          Full Address *
        </label>
        <textarea
          value={shippingInfo.fullAddress}
          onChange={(e) => setShippingInfo(prev => ({ ...prev, fullAddress: e.target.value }))}
          placeholder="Street address, city, state, ZIP code"
          rows={3}
          className={`w-full p-4 bg-gray-50 border ${errors.fullAddress ? 'border-red-500' : 'border-gray-200'} placeholder:text-gray-400 focus:border-[#C6A87C] focus:outline-none focus:ring-1 focus:ring-[#C6A87C] transition-colors resize-none`}
          style={{ color: '#333333' }}
          data-testid="shipping-full-address"
        />
        {errors.fullAddress && <p className="text-xs text-red-500 mt-1">{errors.fullAddress}</p>}
      </div>
    </div>
  );
};

const CartPage = () => {
  const navigate = useNavigate();
  const { items, removeItem, rentalDates, setDates, clearCart } = useCart();
  const { isAuthenticated, token, user } = useAuth();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [insuranceEnabled, setInsuranceEnabled] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    recipientName: '',
    customerEmail: '',
    destinationType: '',
    fullAddress: ''
  });
  const [shippingErrors, setShippingErrors] = useState({});
  const [securityDepositAdded, setSecurityDepositAdded] = useState(false);

  // Pre-fill email from Google auth if signed in
  React.useEffect(() => {
    if (user?.email && !shippingInfo.customerEmail) {
      setShippingInfo(prev => ({ ...prev, customerEmail: user.email }));
    }
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const today = startOfDay(new Date());

  // Calculate shipping tier based on start date
  const shippingTier = useMemo(() => {
    if (!rentalDates.start) return null;
    const daysFromToday = differenceInDays(startOfDay(rentalDates.start), today);
    if (daysFromToday <= BLOCKED_DAYS) return 'blocked';
    if (daysFromToday <= EXPEDITED_CUTOFF_DAYS) return 'expedited';
    return 'standard';
  }, [rentalDates.start, today]);

  const shippingFee = shippingTier === 'expedited' ? EXPEDITED_SHIPPING_FEE : 0;

  // Calculate pricing with discounts and insurance
  const pricingDetails = useMemo(() => {
    if (!rentalDates.start || !rentalDates.end) return null;
    
    const days = differenceInDays(rentalDates.end, rentalDates.start) + 1;
    if (days < MIN_RENTAL_DAYS) return null;
    
    let discountPercent = 0;
    if (days >= 7) discountPercent = 25;
    else if (days >= 3) discountPercent = 15;
    
    const dailyTotal = items.length * DAILY_RATE;
    const subtotal = dailyTotal * days;
    const discountAmount = (subtotal * discountPercent) / 100;
    const rentalTotal = subtotal - discountAmount;
    
    // Insurance calculation
    const insuranceTotal = insuranceEnabled ? (INSURANCE_DAILY_RATE * days * items.length) : 0;
    
    // Final total with shipping, insurance, and security deposit
    const depositAmount = securityDepositAdded ? SECURITY_DEPOSIT_AMOUNT : 0;
    const total = rentalTotal + shippingFee + insuranceTotal + depositAmount;
    
    return {
      days,
      dailyTotal,
      discountPercent,
      subtotal,
      discountAmount,
      rentalTotal,
      insuranceTotal,
      shippingFee,
      depositAmount,
      total,
      returnDate: addDays(rentalDates.end, 2)
    };
  }, [rentalDates, items.length, insuranceEnabled, shippingFee, securityDepositAdded]);

  // Validate shipping form
  const validateShipping = () => {
    const errors = {};
    if (!shippingInfo.recipientName.trim()) {
      errors.recipientName = 'Recipient name is required';
    }
    if (!shippingInfo.customerEmail.trim()) {
      errors.customerEmail = 'Email is required for order updates';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingInfo.customerEmail)) {
      errors.customerEmail = 'Please enter a valid email address';
    }
    if (!shippingInfo.destinationType) {
      errors.destinationType = 'Please select a destination type';
    }
    if (!shippingInfo.fullAddress.trim()) {
      errors.fullAddress = 'Full address is required';
    }
    setShippingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCheckout = async () => {
    if (!rentalDates.start || !rentalDates.end) {
      toast.error('Please select rental dates');
      return;
    }

    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Damage & Loss Waiver');
      return;
    }

    // Validate shipping form
    if (!validateShipping()) {
      toast.error('Please fill in all shipping information');
      return;
    }

    setIsProcessing(true);
    try {
      // Create checkout via backend
      const headers = { 'Content-Type': 'application/json' };
      const savedToken = token || localStorage.getItem('token');
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const payload = {
        items: items.map(item => ({
          id: item.id,
          title: item.title,
          variant_id: item.variant?.id || item.variants?.[0]?.id || '',
          image: item.image_url
        })),
        rental_start: format(rentalDates.start, 'yyyy-MM-dd'),
        rental_end: format(rentalDates.end, 'yyyy-MM-dd'),
        agreed_to_terms: true,
        insurance_enabled: insuranceEnabled,
        security_deposit_added: securityDepositAdded,
        customer_email: shippingInfo.customerEmail,
        shipping_info: {
          recipient_name: shippingInfo.recipientName,
          destination_type: shippingInfo.destinationType,
          full_address: shippingInfo.fullAddress
        },
        origin_url: window.location.origin
      };

      const response = await fetch(`${API}/checkout/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.detail || 'Checkout failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      const checkout_url = data.checkout_url;

      if (checkout_url) {
        // Save order details for the Thank You page
        sessionStorage.setItem('mgr_checkout_success', JSON.stringify({
          checkout_url,
          items: items.map(i => ({ title: i.title, image_url: i.image_url })),
          rental_start: format(rentalDates.start, 'MMM d, yyyy'),
          rental_end: format(rentalDates.end, 'MMM d, yyyy'),
          rental_days: differenceInDays(rentalDates.end, rentalDates.start),
          customer_email: shippingInfo.customerEmail,
          recipient_name: shippingInfo.recipientName,
          pricing: data.pricing,
          security_deposit: securityDepositAdded,
        }));
        clearCart();
        // Redirect to our Thank You page instead of directly to Shopify
        window.location.assign('/checkout/success');
      } else {
        toast.error('Could not create checkout session. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error('Network error — could not reach server. Check your connection.');
      } else if (error.name === 'AbortError') {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error(`Checkout failed: ${error.message || 'Unknown error'}`);
      }
      setIsProcessing(false);
    }
  };

  const isShippingValid = shippingInfo.recipientName.trim() && 
    shippingInfo.destinationType && 
    shippingInfo.fullAddress.trim();

  const hasGearItems = items.some(item => !item.title?.toLowerCase().includes('security deposit'));

  const canCheckout = items.length > 0 && 
    rentalDates.start && 
    rentalDates.end && 
    agreedToTerms && 
    pricingDetails?.days >= MIN_RENTAL_DAYS &&
    isShippingValid &&
    (!hasGearItems || securityDepositAdded);

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="cart-page">
      <div className="px-6 md:px-12 py-12">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="mb-12">
            <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-4">Your Cart</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight" style={{ color: '#333333' }}>
              Rental Cart
            </h1>
          </div>

          {items.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 p-12 text-center">
              <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#333333' }}>Your Cart is Empty</h3>
              <p className="text-gray-500 mb-6">
                Add some gear to get started.
              </p>
              <Link to="/products">
                <Button className="bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider">
                  Browse Gear
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {items.map((item) => (
                  <div 
                    key={`${item.id}-${item.variant?.id}`}
                    className="bg-white border border-gray-200 p-6 flex gap-6"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <div className="w-24 h-24 bg-gray-100 flex-shrink-0 overflow-hidden">
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#666666' }}>
                            {item.vendor}
                          </p>
                          <h3 className="font-['Barlow_Condensed'] text-lg font-semibold uppercase tracking-tight" style={{ color: '#333333' }}>
                            {item.title}
                          </h3>
                          {item.variant && (
                            <p className="text-sm" style={{ color: '#666666' }}>
                              Size: {item.variant.title}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removeItem(item.id, item.variant?.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-${item.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="mt-4">
                        <span className="text-xl font-bold text-[#C6A87C]">
                          ${DAILY_RATE}
                        </span>
                        <span className="text-sm ml-1" style={{ color: '#666666' }}>/day</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-28 bg-white border border-gray-200 p-6 space-y-6" data-testid="cart-summary">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-semibold uppercase tracking-tight flex items-center gap-2" style={{ color: '#333333' }}>
                    <Calendar className="w-5 h-5 text-[#C6A87C]" />
                    Rental Period
                  </h3>

                  <RentalCalendar 
                    bookedDates={[]}
                    onDateChange={(dates) => setDates(dates.start, dates.end)}
                    selectedRange={{ start: rentalDates.start, end: rentalDates.end }}
                    dailyRate={items.length * DAILY_RATE}
                    bufferDays={2}
                  />

                  {/* Insurance Add-on */}
                  {pricingDetails && (
                    <div className="border border-gray-200 bg-gray-50 p-4" data-testid="insurance-section">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div 
                            className={`w-12 h-6 rounded-full relative cursor-pointer transition-colors ${
                              insuranceEnabled ? 'bg-[#C6A87C]' : 'bg-gray-300'
                            }`}
                            onClick={() => setInsuranceEnabled(!insuranceEnabled)}
                            data-testid="insurance-toggle"
                          >
                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                              insuranceEnabled ? 'left-7' : 'left-1'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-[#C6A87C]" />
                              <span className="font-semibold text-sm" style={{ color: '#333333' }}>Damage Protection</span>
                            </div>
                            <p className="text-xs mt-1" style={{ color: '#666666' }}>
                              ${INSURANCE_DAILY_RATE}/day per item
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowInsuranceModal(true)}
                          className="text-[#C6A87C] text-xs underline hover:text-[#D4B88F] flex items-center gap-1"
                          data-testid="learn-more-insurance"
                        >
                          <Info className="w-3 h-3" />
                          Learn More
                        </button>
                      </div>
                      {insuranceEnabled && (
                        <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-sm">
                          <span style={{ color: '#666666' }}>
                            ${INSURANCE_DAILY_RATE} × {pricingDetails.days} days × {items.length} items
                          </span>
                          <span className="text-[#C6A87C] font-semibold">
                            +${pricingDetails.insuranceTotal.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Security Deposit Requirement */}
                  {hasGearItems && !securityDepositAdded && (
                    <div className="border-2 border-red-500 bg-red-50 p-4" data-testid="security-deposit-warning">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-red-600">
                            A $150 Refundable Security Deposit is required for all orders.
                          </p>
                          <button
                            onClick={() => {
                              setSecurityDepositAdded(true);
                              toast.success('Security deposit added to your order');
                            }}
                            className="text-sm font-bold text-red-600 underline hover:text-red-500 mt-1 cursor-pointer"
                            data-testid="add-deposit-btn"
                          >
                            Click Here to Add Deposit
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {securityDepositAdded && (
                    <div className="border border-green-300 p-4 flex items-center justify-between" style={{ backgroundColor: '#e6f9e6' }} data-testid="security-deposit-added">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm font-semibold text-green-700">$150 Refundable Security Deposit</p>
                          <p className="text-xs" style={{ color: '#666666' }}>Returned after gear inspection</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-green-700">${SECURITY_DEPOSIT_AMOUNT}</span>
                        <button
                          onClick={() => {
                            setSecurityDepositAdded(false);
                            toast.info('Security deposit removed');
                          }}
                          className="text-xs text-gray-500 hover:text-red-500 underline"
                          data-testid="remove-deposit-btn"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Shipping Address Form */}
                  <div className="border-t border-gray-200 pt-6">
                    <ShippingForm 
                      shippingInfo={shippingInfo}
                      setShippingInfo={setShippingInfo}
                      errors={shippingErrors}
                    />
                  </div>

                  {/* Order Summary */}
                  {pricingDetails && (
                    <div className="border-t border-gray-200 pt-6 space-y-3" data-testid="order-summary">
                      <h4 className="font-['Barlow_Condensed'] text-sm font-semibold uppercase tracking-wider" style={{ color: '#666666' }}>
                        Order Summary
                      </h4>
                      
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#666666' }}>Rental ({pricingDetails.days} days)</span>
                        <span style={{ color: '#333333' }}>${pricingDetails.subtotal.toFixed(2)}</span>
                      </div>
                      
                      {pricingDetails.discountPercent > 0 && (
                        <div className="flex justify-between text-sm text-[#C6A87C]">
                          <span>Duration Discount ({pricingDetails.discountPercent}%)</span>
                          <span>-${pricingDetails.discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#666666' }}>
                          {shippingTier === 'expedited' ? 'Expedited Shipping' : 'Standard Shipping'}
                        </span>
                        <span className={shippingFee > 0 ? 'text-orange-500' : 'text-green-600'}>
                          {shippingFee > 0 ? `+$${shippingFee.toFixed(2)}` : 'FREE'}
                        </span>
                      </div>
                      
                      {insuranceEnabled && (
                        <div className="flex justify-between text-sm">
                          <span style={{ color: '#666666' }}>Damage Protection</span>
                          <span className="text-[#C6A87C]">+${pricingDetails.insuranceTotal.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {securityDepositAdded && (
                        <div className="flex justify-between text-sm px-2 py-1 rounded" style={{ backgroundColor: '#e6f9e6' }} data-testid="deposit-summary-line">
                          <span className="text-green-700 font-medium">$150 Refundable Security Deposit</span>
                          <span className="text-green-700 font-semibold">+${SECURITY_DEPOSIT_AMOUNT.toFixed(2)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between pt-3 border-t border-gray-200">
                        <span className="font-['Barlow_Condensed'] font-semibold uppercase" style={{ color: '#333333' }}>Total</span>
                        <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#C6A87C]">
                          ${pricingDetails.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Terms of Service & Damage Waiver Checkbox */}
                  <div className="border-t border-gray-200 pt-6" data-testid="terms-checkbox-section">
                    <div className="bg-red-50 border border-red-200 p-4 mb-4">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                        <p className="text-sm font-semibold text-red-600">Damage & Loss Waiver Required</p>
                      </div>
                      <p className="text-xs" style={{ color: '#666666' }}>
                        By checking the box below, you accept responsibility for any damage to or loss of rented gear. 
                        Replacement costs will be charged to your payment method.
                      </p>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="terms" 
                        checked={agreedToTerms}
                        onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                        className="mt-1 border-[#C6A87C] data-[state=checked]:bg-[#C6A87C] data-[state=checked]:text-black"
                        data-testid="terms-checkbox"
                      />
                      <label 
                        htmlFor="terms" 
                        className="text-sm cursor-pointer leading-relaxed"
                        style={{ color: '#555555' }}
                      >
                        I agree to the{' '}
                        <Link 
                          to="/terms" 
                          target="_blank"
                          className="text-[#C6A87C] hover:underline inline-flex items-center gap-1"
                        >
                          Terms of Service
                          <FileText className="w-3 h-3" />
                        </Link>
                        , including the <strong style={{ color: '#333333' }}>Liability Waiver</strong>, 
                        <strong style={{ color: '#333333' }}> Late Fee Policy</strong> (1.5x daily rate), 
                        and <strong style={{ color: '#333333' }}>Damage & Loss Waiver</strong> for any damaged or lost gear.
                      </label>
                    </div>
                    {!agreedToTerms && items.length > 0 && pricingDetails && (
                      <p className="text-xs text-red-500 mt-2">
                        You must agree to proceed with checkout
                      </p>
                    )}
                  </div>

                  {/* Checkout Requirements Notice */}
                  {pricingDetails && !canCheckout && (
                    <div className="bg-[#C6A87C]/10 border border-[#C6A87C]/20 p-3 text-xs text-[#C6A87C]">
                      <p className="font-semibold mb-1">To proceed, please:</p>
                      <ul className="space-y-1">
                        {hasGearItems && !securityDepositAdded && <li>• Add the $150 Security Deposit</li>}
                        {!isShippingValid && <li>• Complete shipping information</li>}
                        {!agreedToTerms && <li>• Agree to Terms of Service</li>}
                      </ul>
                    </div>
                  )}

                  <Button
                    onClick={handleCheckout}
                    disabled={!canCheckout || isProcessing}
                    className={`w-full font-bold uppercase tracking-wider h-14 text-base ${
                      canCheckout && !isProcessing
                        ? 'bg-[#1a1a1a] text-white hover:bg-[#333]' 
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="checkout-btn"
                  >
                    {isProcessing ? (
                      'Processing...'
                    ) : !isShippingValid ? (
                      'Complete Shipping Info'
                    ) : !agreedToTerms && pricingDetails ? (
                      'Agree to Terms to Pay'
                    ) : (
                      <>
                        Pay Now {pricingDetails && `- $${pricingDetails.total.toFixed(2)}`}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center" style={{ color: '#999999' }}>
                    Secure checkout powered by Shopify
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Insurance Modal */}
      <InsuranceModal isOpen={showInsuranceModal} onClose={() => setShowInsuranceModal(false)} />
    </div>
  );
};

export default CartPage;
